import { keys } from 'lodash-es';

/**
 * Returns the dialog's dialog object
 * @param dialog
 * @return {object|undefined}
 */
var getDialog = function getDialog(dialog) {
  return dialog === null || dialog === void 0 ? void 0 : dialog.dialog;
};

/**
 * Returns the dialog's version
 * @param dialog
 * @return {number|undefined}
 */
var getVersion = function getVersion(dialog) {
  var _getDialog;
  return (_getDialog = getDialog(dialog)) === null || _getDialog === void 0 ? void 0 : _getDialog.version;
};

/**
 * Returns the dialog's authenticity token
 * @param dialog
 * @return {string|undefined}
 */
var getAuthenticityToken = function getAuthenticityToken(dialog) {
  var _getDialog2;
  return (_getDialog2 = getDialog(dialog)) === null || _getDialog2 === void 0 ? void 0 : _getDialog2.authenticity_token;
};

/**
 * Returns the dialog's text object
 * @param dialog
 * @returns {object|undefined}
 */
var getText = function getText(dialog) {
  return dialog === null || dialog === void 0 ? void 0 : dialog.text;
};

/**
 * Returns the dialog's back button text
 * @param dialog
 * @returns {string|undefined}
 */
var getBackButtonText = function getBackButtonText(dialog) {
  var _getText;
  return (_getText = getText(dialog)) === null || _getText === void 0 ? void 0 : _getText.back_button;
};

/**
 * Returns the dialog's next button text
 * @param dialog
 * @returns {string|undefined}
 */
var getNextButtonText = function getNextButtonText(dialog) {
  var _getText2;
  return (_getText2 = getText(dialog)) === null || _getText2 === void 0 ? void 0 : _getText2.next_button;
};

/**
 * Returns the dialog's heading
 * @param dialog
 * @returns {string|undefined}
 */
var getHeadingText = function getHeadingText(dialog) {
  var _getText3;
  return (_getText3 = getText(dialog)) === null || _getText3 === void 0 ? void 0 : _getText3.heading;
};

/**
 * Returns the dialog's spinner text
 * @param dialog
 * @return {string|undefined}
 */
var getSpinnerText = function getSpinnerText(dialog) {
  var _getText4;
  return (_getText4 = getText(dialog)) === null || _getText4 === void 0 ? void 0 : _getText4.spinner;
};

/**
 * Returns the opt-in content's text
 * @param dialog
 * @return {string|undefined}
 */
var getOptinContentText = function getOptinContentText(dialog) {
  var _getText5;
  return (_getText5 = getText(dialog)) === null || _getText5 === void 0 ? void 0 : _getText5.optin_content;
};

/**
 * Returns the contact description's text
 * @param dialog
 * @return {string|undefined}
 */
var getContactDescriptionText = function getContactDescriptionText(dialog) {
  var _getText6;
  return (_getText6 = getText(dialog)) === null || _getText6 === void 0 ? void 0 : _getText6.contact_description;
};

/**
 * Returns the additional content's text
 * @param dialog
 * @return {string|undefined}
 */
var getAdditionalContentText = function getAdditionalContentText(dialog) {
  var _getText7;
  return (_getText7 = getText(dialog)) === null || _getText7 === void 0 ? void 0 : _getText7.additional_content;
};

/**
 * Returns options
 * @param dialog
 * @returns {object|undefined}
 */
var getOptions = function getOptions(dialog) {
  return dialog === null || dialog === void 0 ? void 0 : dialog.options;
};

/**
 * Returns the dialog's mandatory field options object
 * @param dialog
 * @return {object|undefined}
 */
var getMandatoryFieldOptions = function getMandatoryFieldOptions(dialog) {
  var _getOptions;
  return (_getOptions = getOptions(dialog)) === null || _getOptions === void 0 ? void 0 : _getOptions.mandatory_fields;
};

/**
 * Returns the spinner duration
 * @param dialog
 * @returns {number|undefined}
 */
var getSpinnerDuration = function getSpinnerDuration(dialog) {
  var _getOptions2;
  return (_getOptions2 = getOptions(dialog)) === null || _getOptions2 === void 0 || (_getOptions2 = _getOptions2.spinner) === null || _getOptions2 === void 0 ? void 0 : _getOptions2.duration;
};

/**
 * Returns all lines
 * @param dialog
 * @returns {object|undefined}
 */
var getLines = function getLines(dialog) {
  return dialog === null || dialog === void 0 ? void 0 : dialog.lines;
};

/**
 * Returns all line keys
 * @param dialog
 * @return {string[]}
 */
var getLineKeys = function getLineKeys(dialog) {
  return keys(getLines(dialog));
};

/**
 * Returns a line
 * @param dialog
 * @param lineKey
 * @returns {object|undefined}
 */
var getLine = function getLine(dialog, lineKey) {
  var _getLines;
  return (_getLines = getLines(dialog)) === null || _getLines === void 0 ? void 0 : _getLines[lineKey];
};

/**
 * Returns a line's title
 * @param dialog
 * @param lineKey
 * @returns {string|undefined}
 */
var getLineTitle = function getLineTitle(dialog, lineKey) {
  var _getLine;
  return (_getLine = getLine(dialog, lineKey)) === null || _getLine === void 0 ? void 0 : _getLine.title;
};

/**
 * Returns a line's icon name
 * @param dialog
 * @param lineKey
 * @returns {string|undefined}
 */
var getLineIcon = function getLineIcon(dialog, lineKey) {
  var _getLine2;
  return (_getLine2 = getLine(dialog, lineKey)) === null || _getLine2 === void 0 ? void 0 : _getLine2.icon;
};

/**
 * Returns a line's icon as plain svg string
 * @param dialog
 * @param lineKey
 * @returns {string|undefined}
 */
var getLineIconSVG = function getLineIconSVG(dialog, lineKey) {
  var _getLine3;
  return (_getLine3 = getLine(dialog, lineKey)) === null || _getLine3 === void 0 ? void 0 : _getLine3.icon_svg;
};

/**
 * Returns the steps from a line (key)
 * @param dialog
 * @param lineKey
 * @returns {object|undefined}
 */
var getSteps = function getSteps(dialog, lineKey) {
  var _getLine4;
  return (_getLine4 = getLine(dialog, lineKey)) === null || _getLine4 === void 0 ? void 0 : _getLine4.steps;
};

/**
 * Returns the step from a line (key) and step (key)
 * @param dialog
 * @param lineKey
 * @param stepKey
 * @returns {object|undefined}
 */
var getStep = function getStep(dialog, lineKey, stepKey) {
  var _getSteps;
  return (_getSteps = getSteps(dialog, lineKey)) === null || _getSteps === void 0 ? void 0 : _getSteps[stepKey];
};

/**
 * Returns an array of step keys from a line (key)
 * @param dialog
 * @param lineKey
 * @returns {string[]|undefined}
 */
var getStepKeys = function getStepKeys(dialog, lineKey) {
  return keys(getSteps(dialog, lineKey));
};

/**
 * Returns the previous step key
 * @param dialog
 * @param lineKey
 * @param stepKey
 * @returns {string|undefined}
 */
var getPreviousStepKey = function getPreviousStepKey(dialog, lineKey, stepKey) {
  var stepKeys = getStepKeys(dialog, lineKey);
  return stepKeys[stepKeys.indexOf(stepKey) - 1];
};

/**
 * Returns the next step key
 * @param dialog
 * @param lineKey
 * @param stepKey
 * @returns {string|undefined}
 */
var getNextStepKey = function getNextStepKey(dialog, lineKey, stepKey) {
  var stepKeys = getStepKeys(dialog, lineKey);
  return stepKeys[stepKeys.indexOf(stepKey) + 1];
};

/**
 * Returns the next step position
 * @param dialog
 * @param lineKey
 * @param stepKey
 * @returns {string|undefined}
 */
var getNextStepPosition = function getNextStepPosition(dialog, lineKey, stepKey) {
  var nextStepKey = getNextStepKey(dialog, lineKey, stepKey);
  var nextStep = getStep(dialog, lineKey, nextStepKey);
  return nextStep === null || nextStep === void 0 ? void 0 : nextStep.position;
};

/**
 * Returns a boolean on if navigation buttons should be shown
 * @param dialog
 * @returns {boolean|undefined}
 */
var shouldShowNavigationButtons = function shouldShowNavigationButtons(dialog) {
  var _getOptions3;
  return (_getOptions3 = getOptions(dialog)) === null || _getOptions3 === void 0 ? void 0 : _getOptions3.back_next_buttons;
};

/**
 * Returns the opt-in type
 * @param dialog
 * @return {string|undefined}
 */
var getOptinType = function getOptinType(dialog) {
  var _getOptions4;
  return (_getOptions4 = getOptions(dialog)) === null || _getOptions4 === void 0 ? void 0 : _getOptions4.optin_type;
};
export { getAdditionalContentText, getAuthenticityToken, getBackButtonText, getContactDescriptionText, getDialog, getHeadingText, getLine, getLineIcon, getLineIconSVG, getLineKeys, getLineTitle, getLines, getMandatoryFieldOptions, getNextButtonText, getNextStepKey, getNextStepPosition, getOptinContentText, getOptinType, getOptions, getPreviousStepKey, getSpinnerDuration, getSpinnerText, getStep, getStepKeys, getSteps, getText, getVersion, shouldShowNavigationButtons };